import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Modal } from '../ui/Modal';
import { useDataContext } from '../../context/data';
import translations from '../../constants/translations.json';
import useSubscription from '../../hooks/useSubscription';
import useTrialPeriod from '../../hooks/useTrialPeriod';
import { DEFAULT_MONTHLY_PRICE, fillTemplate } from '../../utils/trialPeriod';
import { isSubscriptionActive, syncCachedSubscribed } from '../../utils/heywaitrPlus';

const LOCALE_MAP = { da: 'da', no: 'nb', en: 'en', de: 'de', sv: 'sv', es: 'es' };
const TERMS_URL = 'https://www.heywaitr.com/heywaitr-app-global-terms-and-conditions/';

const formatDate = (value, language) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  try {
    return d.toLocaleDateString(LOCALE_MAP[language] || 'en', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch (e) {
    return d.toDateString();
  }
};

const prettyPlanName = (plan) =>
  typeof plan === 'string' ? plan.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '';

/**
 * HeyWaitr+ membership panel for the Profile page. Inspired by the heywaitr-receipt
 * <Profile /> flow (live plan list, terms gate, status + cancel/resume) but styled
 * to match heywaitr-web for uniformity. Subscription management happens in-app.
 */
export default function SubscriptionSection({ token, initialSubscribed, onLoaded }) {
  const { language, setUserProfile } = useDataContext();
  const t = (key, fallback) => translations[language]?.[key] || fallback;

  // Sync the cached profile flag + context after a cancel/resume so the navbar
  // reflects it without a reload (mirrors Navbar's mount-time reconciliation).
  const syncProfileSubscribed = (data) => {
    const merged = syncCachedSubscribed(isSubscriptionActive(data));
    if (merged) setUserProfile?.(merged);
  };

  const { subscription, loading, actionLoading, plans, primaryPlan, subscribe, cancel, resume } =
    useSubscription(token, initialSubscribed);
  // Live offer copy: trial length from the backend's /banners text, monthly
  // price from the live plan list (both fall back to the last known offer).
  const trialPeriod = useTrialPeriod();
  const monthlyPrice = primaryPlan?.price ?? DEFAULT_MONTHLY_PRICE;

  // Notify the parent once the subscription data is in (e.g. to scroll here).
  useEffect(() => {
    if (!loading) onLoaded?.();
  }, [loading, onLoaded]);

  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showMustAgree, setShowMustAgree] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);

  const Logo = (
    <div className="inline-flex items-center justify-center">
      <img src="/logo-icon.png" className="w-7" alt="Heywaitr icon" />
      <p className="text-2xl font-semibold">
        heywaitr<span className="text-[#e5ca17]">+</span>
      </p>
    </div>
  );

  if (!token) return null;

  const cardClass = 'bg-white rounded-2xl pt-7 shadow-sm';

  if (loading) {
    return (
      <div className={`${cardClass} flex items-center gap-3 text-gray-400`}>
        <i className="fa fa-circle-notch fa-spin" />
        <span className="text-sm">{t('heywaitrplus_loading', 'Loading membership…')}</span>
      </div>
    );
  }

  const handleSubscribe = async (plan) => {
    if (!agreedToTerms) {
      setShowMustAgree(true);
      return;
    }
    try {
      await subscribe(plan);
    } catch (e) {
      toast.error(t('heywaitrplus_action_error', 'Something went wrong. Please try again.'));
    }
  };

  const handleCancel = async () => {
    setConfirmCancel(false);
    try {
      syncProfileSubscribed(await cancel());
      toast.success(t('heywaitrplus_cancel_success', 'Your membership has been cancelled.'));
    } catch (e) {
      toast.error(t('heywaitrplus_action_error', 'Something went wrong. Please try again.'));
    }
  };

  const handleResume = async () => {
    try {
      syncProfileSubscribed(await resume());
      toast.success(t('heywaitrplus_resume_success', 'Your membership has been resumed.'));
    } catch (e) {
      toast.error(t('heywaitrplus_action_error', 'Something went wrong. Please try again.'));
    }
  };

  // ── Active / cancelled member ──────────────────────────────────────────────
  if (subscription) {
    const isCancelled = subscription.is_cancelled;
    const inTrial = subscription.in_trial;
    const nextDate = formatDate(subscription.next_period_start, language);
    const statusText = isCancelled
      ? t('heywaitrplus_status_cancelled_short', 'Cancelled')
      : inTrial
        ? t('heywaitrplus_status_trial', 'Free trial')
        : t('heywaitrplus_status_active', 'Active');

    return (
      <>
        <div className={cardClass}>
          <h3 className="text-lg text-[#1a1a1a] mb-4">{Logo}</h3>

          <dl className="space-y-3 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="text-gray-400">{t('heywaitrplus_plan_label', 'Plan')}</dt>
              <dd className="font-semibold text-[#1a1a1a] text-right">
                {prettyPlanName(subscription.plan) || '—'}
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-gray-400">{t('heywaitrplus_status_label', 'Status')}</dt>
              <dd className="text-right">
                <span
                  className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                    isCancelled ? 'bg-amber-50 text-amber-600' : 'bg-primary/10 text-secondary'
                  }`}
                >
                  {statusText}
                </span>
              </dd>
            </div>
            {nextDate && !isCancelled && (
              <div className="flex justify-between gap-4">
                <dt className="text-gray-400">{t('heywaitrplus_next_payment', 'Next payment')}</dt>
                <dd className="font-semibold text-[#1a1a1a] text-right">{nextDate}</dd>
              </div>
            )}
          </dl>

          {subscription.state === 'active' && (
            <div className="mt-5">
              {isCancelled ? (
                <button
                  type="button"
                  disabled={actionLoading}
                  onClick={handleResume}
                  className="w-full min-h-10 py-2.5 px-6 text-[13px] font-semibold bg-primary hover:bg-secondary text-white rounded-full transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
                >
                  {actionLoading ? (
                    <i className="fa fa-circle-notch fa-spin" />
                  ) : (
                    t('heywaitrplus_resume', 'Resume membership')
                  )}
                </button>
              ) : (
                <button
                  type="button"
                  disabled={actionLoading}
                  onClick={() => setConfirmCancel(true)}
                  className="w-full min-h-10 py-2.5 px-6 text-[13px] font-semibold border border-[#e0e0e0] text-[#333] rounded-full transition-all hover:border-red-300 hover:text-red-500 disabled:opacity-60"
                >
                  {t('heywaitrplus_cancel', 'Cancel membership')}
                </button>
              )}
            </div>
          )}
        </div>

        <Modal
          isOpen={confirmCancel}
          onClose={() => setConfirmCancel(false)}
          contentClassName="max-w-[420px]"
          noCloseButton
        >
          <div className="px-6 pt-8 pb-6 text-center">
            <h3 className="text-lg font-bold text-[#1a1a1a] mb-2">
              {t('heywaitrplus_cancel_confirm_title', 'Cancel membership?')}
            </h3>
            <p className="text-sm text-gray-500 mb-6">
              {t(
                'heywaitrplus_cancel_confirm_desc',
                "You'll keep access to your deals until the end of the current period.",
              )}
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setConfirmCancel(false)}
                className="flex-1 py-3 border border-gray-200 text-gray-600 font-semibold rounded-xl text-sm"
              >
                {t('heywaitrplus_keep', 'Keep membership')}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="flex-1 py-3 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-xl text-sm"
              >
                {t('heywaitrplus_cancel_confirm', 'Yes, cancel')}
              </button>
            </div>
          </div>
        </Modal>
      </>
    );
  }

  // ── Non-member: plan picker (gated by Terms agreement) ─────────────────────
  return (
    <>
      <div className={cardClass}>
        <h3 className="text-lg text-[#1a1a1a] mb-1">{Logo}</h3>
        <p className="text-[13px] text-gray-400 mb-5">
          {fillTemplate(
            t(
              'heywaitrplus_upsell_desc',
              'Unlock exclusive daily deals at your favourite places. {period} free, then only {price} kr/mo.',
            ),
            { period: trialPeriod.label, price: monthlyPrice },
          )}
        </p>

        {/* Terms agreement */}
        <label className="flex items-start gap-2.5 mb-5 cursor-pointer">
          <input
            type="checkbox"
            checked={agreedToTerms}
            onChange={(e) => setAgreedToTerms(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded accent-primary cursor-pointer"
          />
          <span className="text-[13px] text-gray-600">
            {t('heywaitrplus_agree_terms_pre', 'I agree to the')}{' '}
            <a
              href={TERMS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline hover:text-secondary"
            >
              {t('heywaitrplus_terms', 'Terms & Conditions')}
            </a>
          </span>
        </label>

        {/* Plan grid */}
        {plans.length === 0 ? (
          <div className="flex items-center gap-3 text-gray-400 text-sm py-4">
            <i className="fa fa-circle-notch fa-spin" />
            {t('heywaitrplus_loading', 'Loading membership…')}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {plans.map((plan) => {
              const per = /yearly|year/i.test(plan.id)
                ? t('heywaitrplus_per_year', 'year')
                : t('heywaitrplus_per_month', 'month');
              return (
                <div
                  key={plan.id}
                  className="border border-gray-200 rounded-xl p-4 text-center flex flex-col"
                >
                  <h4 className="font-semibold text-[#1a1a1a]">{plan.name}</h4>
                  <div className="flex items-baseline justify-center gap-1 my-2">
                    <span className="text-2xl font-bold text-primary">DKK {plan.price}</span>
                    <span className="text-sm text-gray-400">/ {per}</span>
                  </div>
                  <button
                    type="button"
                    disabled={actionLoading}
                    onClick={() => handleSubscribe(plan)}
                    className="mt-auto w-full py-2.5 text-[13px] font-semibold bg-primary hover:bg-secondary text-white rounded-full transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
                  >
                    {actionLoading && <i className="fa fa-circle-notch fa-spin" />}
                    {t('heywaitrplus_subscribe', 'Subscribe')}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Modal
        isOpen={showMustAgree}
        onClose={() => setShowMustAgree(false)}
        contentClassName="max-w-[420px]"
        noCloseButton
      >
        <div className="px-6 pt-8 pb-6 text-center">
          <h3 className="text-lg font-bold text-[#1a1a1a] mb-2">
            {t('heywaitrplus_must_agree_title', 'Terms & Conditions required')}
          </h3>
          <p className="text-sm text-gray-500 mb-6">
            {t(
              'heywaitrplus_must_agree_desc',
              'You must agree to the Terms & Conditions before subscribing.',
            )}
          </p>
          <button
            type="button"
            onClick={() => setShowMustAgree(false)}
            className="w-full py-3 bg-primary hover:bg-secondary text-white font-semibold rounded-xl text-sm"
          >
            {t('heywaitrplus_understood', 'I understood')}
          </button>
        </div>
      </Modal>
    </>
  );
}
